import {
  computeSalaryStructureSimulation,
  computeEmployerStatutory,
  buildEmployerPfContributionRows,
  computeStructureMinimumCtc,
  resolvePfWageBase,
  resolveGratuityWageBase,
  computePfEmployeeDeduction,
  roundAmount,
  isRecurrentComponent,
} from "../components/Admin/employee-management/salary-structure/utils/salaryStructureSimulatorCompute";
import {
  buildSimulatorStatutoryConfig,
  computeEsiAmounts,
  computePtAmount,
  loadStatutoryRatesFromStorage,
  resolveStatutoryRatesForEffectiveDate,
  effectiveDateFromMonth,
} from "../components/Admin/employee-management/salary-structure/utils/statutoryRatesDefaults";
import { resolvePtStateFromEmployee } from "../components/Admin/employee-management/salary-structure/utils/ptStates";
import formulaEngine, { formulaToNames } from "../components/Admin/employee-management/salary-structure/utils/FormulaEngine";
import {
  resolveEmployeeNpsPercent,
  resolveEmployeeMealMonthly,
} from "./salaryStructureAddons";
import { computeStructurePfForPayMonth } from "../components/Admin/employee-management/salary-structure/utils/structurePfMonthCalc";

function hasCategory(component, key) {
  if (component?.categories && typeof component.categories === "object") {
    return !!component.categories[key];
  }
  const raw = String(component?.category || "").toUpperCase();
  if (key === "earning") return raw === "EARNING";
  if (key === "deduction") return raw === "DEDUCTION";
  if (key === "benefit") return raw === "BENEFIT" || raw === "EMPLOYER_BENEFIT";
  if (key === "reimbursement") return raw === "REIMBURSEMENT";
  return false;
}

function withAnnual(rows = []) {
  return rows.map((r) => ({
    ...r,
    amount: roundAmount(r.amount),
    annualAmount: roundAmount((Number(r.amount) || 0) * 12),
  }));
}

function buildLists(components, amountsByComponentId) {
  let gross = 0;
  const earningsList = [];
  const deductionsList = [];
  const benefitsList = [];
  const reimbursementsList = [];
  const employerList = [];

  components.forEach((c) => {
    if (isRecurrentComponent(c)) return;
    const amount = amountsByComponentId[c.id] || 0;
    const calcType = String(c.calculationType || "").toLowerCase();
    const isManual =
      calcType === "manual" &&
      !c.autoAdjustCTC &&
      !isRecurrentComponent(c);
    const row = {
      name: c.name,
      amount,
      componentId: c.id,
      calculationType: c.calculationType || "manual",
      editable: isManual && !c.formulaOverride,
      formulaOverride: !!c.formulaOverride,
    };
    if (hasCategory(c, "earning")) {
      earningsList.push(row);
      gross += amount;
    } else if (hasCategory(c, "deduction")) {
      deductionsList.push(row);
    } else if (hasCategory(c, "benefit")) {
      benefitsList.push(row);
      gross += amount;
    } else if (hasCategory(c, "reimbursement")) {
      reimbursementsList.push(row);
      gross += amount;
    }
  });

  return {
    gross: roundAmount(gross),
    earningsList: withAnnual(earningsList),
    deductionsList: withAnnual(deductionsList),
    benefitsList: withAnnual(benefitsList),
    reimbursementsList: withAnnual(reimbursementsList),
    employerList: withAnnual(employerList),
  };
}

function buildStatutoryLists(
  components,
  amountsByComponentId,
  config,
  applicability,
  excludeBalanceId = null,
  {
    gender = null,
    month = null,
    statutoryConfigJson = null,
    payMonth = null,
    pfEmployerOverride = null,
    pfEmployeeOverride = null,
  } = {}
) {
  // Include Special in statutory bases only when ≥ 0 (same rule as simulator).
  const amounts = { ...amountsByComponentId };
  if (excludeBalanceId != null) {
    amounts[excludeBalanceId] = Math.max(0, Number(amountsByComponentId[excludeBalanceId]) || 0);
  }
  const pfBase = resolvePfWageBase(components, amounts, applicability);
  const gratuityBase = resolveGratuityWageBase(components, amounts, applicability);

  // ESI eligibility: fixed (non–Monthly Variable) earnings excluding balance.
  // ESI contribution: components marked esiApplicable (fixed + Monthly Variable).
  let eligibilityFixedGross = 0;
  let contributionWage = 0;
  components.forEach((c) => {
    if (excludeBalanceId != null && c.id === excludeBalanceId) return;
    if (
      !(
        hasCategory(c, "earning") ||
        hasCategory(c, "benefit") ||
        hasCategory(c, "reimbursement")
      )
    ) {
      return;
    }
    const amt = amounts[c.id] || 0;
    if (!(amt > 0)) return;
    if (!isRecurrentComponent(c)) {
      eligibilityFixedGross += amt;
    }
    if (c.esiApplicable === true) {
      contributionWage += amt;
    }
  });
  eligibilityFixedGross = Math.max(0, roundAmount(eligibilityFixedGross));
  contributionWage = Math.max(0, roundAmount(contributionWage));
  const grossForEsi = contributionWage;

  let statutory = computeEmployerStatutory(
    pfBase,
    gratuityBase,
    grossForEsi,
    config,
    {
      ...applicability,
      esiEligibilityGross: eligibilityFixedGross,
    }
  );
  let pfEmployeeAmount = computePfEmployeeDeduction(
    pfBase,
    config,
    applicability
  );
  // Prefer mid-month package PF from simulator balance (same as SA room).
  if (pfEmployerOverride != null && Number(pfEmployerOverride) >= 0) {
    statutory = {
      ...statutory,
      pfEmployer: Number(pfEmployerOverride),
      total: roundAmount(
        Number(pfEmployerOverride) + statutory.esiEmployer + statutory.gratuity
      ),
    };
  }
  if (pfEmployeeOverride != null && Number(pfEmployeeOverride) >= 0) {
    pfEmployeeAmount = Number(pfEmployeeOverride);
  } else if (statutoryConfigJson && payMonth) {
    const monthPf = computeStructurePfForPayMonth({
      pfWageMonthly: pfBase,
      statutoryConfigJson,
      payMonth,
      applicability,
    });
    if (monthPf) {
      pfEmployeeAmount = monthPf.employee;
      statutory = {
        ...statutory,
        pfEmployer: monthPf.employer,
        total: roundAmount(
          monthPf.employer + statutory.esiEmployer + statutory.gratuity
        ),
      };
    }
  }

  const esiParts = computeEsiAmounts(
    contributionWage,
    config.esi,
    applicability.esiApplicable,
    { eligibilityGross: eligibilityFixedGross }
  );
  const payMonthNum = Number(month) || new Date().getMonth() + 1;
  const ptAmount = roundAmount(
    computePtAmount({
      monthlyGross: eligibilityFixedGross > 0 ? eligibilityFixedGross : grossForEsi,
      ptStateConfig: config.ptStateConfig,
      ptSlabs: config.ptSlabs,
      gender,
      month: payMonthNum,
      ptApplicable: applicability.ptApplicable,
      stateName: config.ptState,
    })
  );

  const statutoryDeductionsList = withAnnual([
    ...(applicability.pfApplicable
      ? [{ name: "PF (Employee)", amount: pfEmployeeAmount }]
      : []),
    ...(applicability.esiApplicable
      ? [{ name: "ESI (Employee)", amount: esiParts.employee }]
      : []),
    ...(applicability.ptApplicable
      ? [{ name: "Professional Tax (PT)", amount: ptAmount }]
      : []),
  ]);
  const statutoryEmployerList = withAnnual([
    ...buildEmployerPfContributionRows(statutory, applicability.pfApplicable),
    ...(applicability.esiApplicable
      ? [{ name: "ESI (Employer)", amount: roundAmount(esiParts.employer) }]
      : []),
    ...(applicability.gratuityApplicable
      ? [{ name: "Gratuity", amount: roundAmount(statutory.gratuity) }]
      : []),
  ]);

  return {
    statutoryDeductionsList,
    statutoryEmployerList,
    statutory,
    esiParts,
    grossForEsi,
    pfWageBase: pfBase,
    gratuityWageBase: gratuityBase,
  };
}

function describeComponentBasis(component, structureComponents = []) {
  if (!component) return "—";
  const formulaText = formulaToNames(component.selectedFormula?.formula, structureComponents);
  if (component.autoAdjustCTC) {
    if (component.calculationType === "formula" && component.selectedFormula?.formula) {
      const fname = component.selectedFormula.name
        ? `${component.selectedFormula.name}: `
        : "";
      return `Balance CTC (cap — ${fname}${formulaText})`;
    }
    if (component.manualPercent) {
      return `Balance CTC (cap — ${component.manualPercent}% of ${component.percentBasis || "CTC"})`;
    }
    return "Balance of CTC (remaining after other earnings + employer cost)";
  }
  if (component.calculationType === "formula" && component.selectedFormula?.formula) {
    const fname = component.selectedFormula.name
      ? `${component.selectedFormula.name}: `
      : "";
    return `Formula — ${fname}${formulaText}`;
  }
  if (component.calculationType === "manual" || !component.calculationType) {
    if (component.calculationMethod === "percentage" || component.manualPercent) {
      const pct = component.manualPercent ?? "";
      const basis = component.percentBasis || "CTC";
      return `Variable — ${pct}% of ${basis}`;
    }
    if (component.manualAmount != null && component.manualAmount !== "") {
      return `Fixed / Manual — ₹${Number(component.manualAmount).toLocaleString("en-IN")}`;
    }
  }
  return "As configured in salary structure";
}

function buildComponentCalculationRows(structureComponents, earningsList, reimbursementsList, benefitsList) {
  const byName = new Map(
    (structureComponents || []).map((c) => [String(c.name || "").toLowerCase(), c])
  );
  const rows = [];
  const push = (item, section) => {
    const comp = byName.get(String(item.name || "").toLowerCase());
    rows.push({
      section,
      name: item.name,
      monthly: item.amount,
      annual: item.annualAmount ?? roundAmount((item.amount || 0) * 12),
      basis: describeComponentBasis(comp, structureComponents),
      pfApplicable: !!comp?.pfApplicable,
      gratuityApplicable: !!comp?.gratuityApplicable,
    });
  };
  (earningsList || []).forEach((i) => push(i, "Earnings (E)"));
  (reimbursementsList || []).forEach((i) => push(i, "Reimbursements (R)"));
  (benefitsList || []).forEach((i) => push(i, "Benefits"));
  return rows;
}

/**
 * Build simulator-style breakdown for an employee + matched structure.
 */
export function buildEmployeeStructureBreakdown({
  structure,
  annualAmount,
  salaryType = "ctc",
  employee = {},
  minimumWageAmount = null,
  statutoryRates = null,
  statutoryConfigJson = null,
  effectiveMonth = null,
  ptState = null,
  branchesList = [],
}) {
  const components = Array.isArray(structure?.components) ? structure.components : [];
  const annual = Number(annualAmount) || 0;
  const pbAmount = Number(
    employee.performanceBonus ??
      employee.performanceBonusAmount ??
      employee.performance_bonus ??
      0
  );
  const pbPeriod = String(
    employee.performanceBonusPeriod ||
      employee.performance_bonus_period ||
      "annual"
  ).toLowerCase();

  const effectiveKey = effectiveDateFromMonth(
    effectiveMonth || structure?.effectiveMonth || new Date().toISOString().slice(0, 7)
  );
  const resolvedPtState =
    ptState ||
    resolvePtStateFromEmployee(employee, branchesList) ||
    null;

  let rates;
  if (statutoryConfigJson) {
    rates = resolveStatutoryRatesForEffectiveDate(statutoryConfigJson, effectiveKey);
  } else {
    rates = statutoryRates || loadStatutoryRatesFromStorage();
  }

  const config = buildSimulatorStatutoryConfig(
    rates,
    resolvedPtState || rates.pt?.currentState || "Karnataka"
  );
  const esiCutoff =
    Number(config.esi?.cutoff ?? config.esi?.ceiling ?? config.esi?.eligibilityGrossMonthly) ||
    21000;

  let applicability = {
    pfApplicable: employee.pfApplicable !== false,
    esiApplicable: employee.esiApplicable === true,
    ptApplicable: employee.ptApplicable !== false,
    gratuityApplicable: employee.gratuityApplicable !== false,
    // Ceiling PF on employee profile (statutory wage ceiling)
    ceilingPf: (() => {
      const ceiling = employee.ceilingPf ?? employee.ceiling_pf;
      if (ceiling == null) return true; // DB default ceiling_pf = true
      return ceiling !== false && Number(ceiling) !== 0;
    })(),
  };

  const mode = String(salaryType || structure?.salaryType || "ctc").toLowerCase();
  const isCtcMode = mode !== "gross";

  // Fixed CTC non-payroll add-ons (Employer NPS / Meal) — reserved out of Balance CTC in the sim
  const empNps =
    employee?.employeeNps ||
    employee?.statutoryBenefits?.employerNps ||
    null;
  const npsPct = resolveEmployeeNpsPercent(structure?.nps, empNps);
  const empMeal =
    employee?.employeeMealVoucher ||
    employee?.employeeMeal ||
    employee?.statutoryBenefits?.mealVoucher ||
    null;
  const mealAmt = resolveEmployeeMealMonthly(structure?.mealVoucher, empMeal);
  const npsFromBasic = (basic) =>
    npsPct > 0 && basic > 0 ? roundAmount((basic * npsPct) / 100) : 0;

  const runSim = (appl) =>
    computeSalaryStructureSimulation({
      components,
      salaryType: mode,
      ctcValue: annual,
      ctcPeriod: "annual",
      statutoryConfig: config,
      applicability: appl,
      formulaEngine,
      minimumWageAmount,
      asOfDate: effectiveKey,
      performanceBonusAmount:
        structure?.performanceBonus?.enabled === false ? 0 : pbAmount,
      performanceBonusPeriod: pbPeriod === "monthly" ? "monthly" : "annual",
      addonReserveFromBasic: (basic) => npsFromBasic(basic) + roundAmount(mealAmt),
      statutoryConfigJson,
    });

  let sim = runSim(applicability);
  let lists = buildLists(components, sim.amountsByComponentId);
  // ESI only when employee flag is explicitly on. Cutoff gates eligibility + caps
  // contribution (see computeEsiAmounts). Do not auto-force ESI when flag is off.
  const esiAutoEnabled = false;

  const {
    statutoryDeductionsList,
    statutoryEmployerList,
    statutory,
    grossForEsi,
    pfWageBase,
    gratuityWageBase,
  } = buildStatutoryLists(
    components,
    sim.amountsByComponentId,
    config,
    applicability,
    sim.balanceComponentId,
    {
      gender:
        employee?.user?.gender ||
        employee?.gender ||
        employee?.User?.gender ||
        null,
      month: Number(String(effectiveKey).slice(5, 7)) || null,
      statutoryConfigJson,
      payMonth: String(effectiveKey).slice(0, 7),
      pfEmployerOverride: sim.statutoryEmployerUsedForBalance?.pfEmployer,
      pfEmployeeOverride: sim.statutoryEmployerUsedForBalance?._pfEmployeeOverride,
    }
  );

  const structureMin = isCtcMode
    ? computeStructureMinimumCtc({
        statutoryConfig: config,
        applicability,
        minimumWageAmount,
      })
    : null;

  const totalDeductions =
    lists.deductionsList.reduce((s, r) => s + r.amount, 0) +
    statutoryDeductionsList.reduce((s, r) => s + r.amount, 0);

  // Fixed CTC non-payroll add-ons (Employer NPS / Meal) — match payroll Process + structure form
  const addonEmployerRows = [];
  const basicRow = (lists.earningsList || []).find((r) =>
    /^basic(\s+salary|\s+pay)?$/i.test(String(r.name || "").trim())
  );
  const basicMonthly = Number(basicRow?.amount) || 0;
  const npsMonthly = npsFromBasic(sim.basicAmount ?? basicMonthly);
  if (npsMonthly > 0) {
    addonEmployerRows.push({
      name: "NPS (Employer)",
      amount: npsMonthly,
      nonPayroll: true,
    });
  }
  if (mealAmt > 0) {
    addonEmployerRows.push({
      name: "Meal Voucher",
      amount: roundAmount(mealAmt),
      nonPayroll: true,
    });
  }
  const employerListWithAddons = withAnnual([
    ...(lists.employerList || []),
    ...addonEmployerRows,
  ]);

  const employerShares =
    employerListWithAddons.reduce((s, r) => s + r.amount, 0) +
    statutoryEmployerList.reduce((s, r) => s + r.amount, 0);

  const grossMonthly = roundAmount(lists.gross);
  const deductionsMonthly = roundAmount(totalDeductions);
  const employerMonthly = roundAmount(employerShares);
  const netMonthly = roundAmount(grossMonthly - deductionsMonthly);
  const actualCtcMonthly = roundAmount(grossMonthly + employerMonthly);

  const balanceId = sim.balanceComponentId;
  const balanceAmount =
    balanceId != null ? Number(sim.amountsByComponentId[balanceId] || 0) : 0;
  const hasNegativeBalance = balanceAmount < 0;

  let complianceError = null;
  if (isCtcMode && structureMin && annual > 0 && annual < structureMin.annual) {
    complianceError = `CTC is below the minimum wages CTC of ₹${structureMin.annual.toLocaleString("en-IN")}/year (₹${structureMin.monthly.toLocaleString("en-IN")}/mo = Basic MW + PF/Gratuity/ESI employer). Raise CTC before saving.`;
  } else if (hasNegativeBalance) {
    complianceError = `Special Allowance is negative (₹${balanceAmount.toLocaleString("en-IN")}/mo). Profile CTC is below the minimum. Raise CTC before saving.`;
  }

  const componentCalculations = buildComponentCalculationRows(
    components,
    lists.earningsList,
    lists.reimbursementsList,
    lists.benefitsList
  );

  const pfBreakdown = statutory?.pfBreakdown || {};
  const wageForPfDisplay = applicability.ceilingPf
    ? Math.min(pfWageBase, Number(config.pf?.pfWageCeiling ?? 15000) || 15000)
    : pfWageBase;

  return {
    structureName: structure?.name || "",
    monthlyCtc: sim.monthlyCtc,
    annualAmount: annual,
    amountsByComponentId: sim.amountsByComponentId,
    balanceComponentId: balanceId ?? null,
    earningsList: lists.earningsList,
    deductionsList: lists.deductionsList,
    benefitsList: lists.benefitsList,
    reimbursementsList: lists.reimbursementsList,
    employerList: employerListWithAddons,
    statutoryDeductionsList,
    statutoryEmployerList,
    componentCalculations,
    calculationBasis: {
      mode: isCtcMode ? "CTC" : "Gross",
      profileAnnual: annual,
      profileMonthly: sim.monthlyCtc,
      effectiveMonth: effectiveKey.slice(0, 7),
      ptState: config.ptState,
      minimumWageAmount: minimumWageAmount != null ? Number(minimumWageAmount) : null,
      pfWageBase: roundAmount(pfWageBase),
      pfWageUsed: roundAmount(wageForPfDisplay),
      gratuityWageBase: roundAmount(gratuityWageBase),
      grossForEsiPt: roundAmount(grossForEsi),
      rates: {
        pfEmployeeShare: Number(config.pf?.employeeShare ?? 12),
        pfEmployerShare: Number(config.pf?.employerShare ?? 12),
        pfAdminCharges: Number(config.pf?.adminCharges ?? 0.5),
        pfEdliRate: Number(config.pf?.edliRate ?? 0.5),
        pfWageCeiling: Number(config.pf?.pfWageCeiling ?? config.pf?.ceiling ?? 15000),
        edliWageCeiling: Number(config.pf?.edliWageCeiling ?? 15000),
        gratuityRate: Number(config.gratuity?.rate ?? 4.81),
        esiEmployeeShare: Number(config.esi?.employeeShare ?? 0.75),
        esiEmployerShare: Number(config.esi?.employerShare ?? 3.25),
        esiCutoff: esiCutoff,
      },
      pfEmployerDetail: {
        employerShare: roundAmount(pfBreakdown.employerShare || 0),
        adminCharges: roundAmount(pfBreakdown.adminCharges || 0),
        edliCharges: roundAmount(pfBreakdown.edliCharges || 0),
        total: roundAmount(statutory?.pfEmployer || 0),
        note: applicability.ceilingPf
          ? `Ceiling PF On — wage used min(PF base, ₹${Number(config.pf?.pfWageCeiling ?? 15000).toLocaleString("en-IN")})`
          : "Ceiling PF Off — wage used = full PF wage base",
      },
    },
    totals: {
      earnings: lists.earningsList.reduce((s, r) => s + r.amount, 0),
      deductions: deductionsMonthly,
      benefits: lists.benefitsList.reduce((s, r) => s + r.amount, 0),
      reimbursements: lists.reimbursementsList.reduce((s, r) => s + r.amount, 0),
      gross: grossMonthly,
      grossAnnual: roundAmount(grossMonthly * 12),
      net: netMonthly,
      netAnnual: roundAmount(netMonthly * 12),
      employerShares: employerMonthly,
      employerSharesAnnual: roundAmount(employerMonthly * 12),
      deductionsAnnual: roundAmount(deductionsMonthly * 12),
      monthlyCtc: sim.monthlyCtc,
      annualCtc: annual,
      actualCtcMonthly,
      actualCtcAnnual: roundAmount(actualCtcMonthly * 12),
      // Subtotal (E) for Gross match — earnings + non-employer benefits only
      earningsSubtotal: roundAmount(
        lists.earningsList.reduce((s, r) => s + r.amount, 0) +
          lists.benefitsList.reduce((s, r) => s + r.amount, 0)
      ),
      earningsSubtotalAnnual: roundAmount(
        (lists.earningsList.reduce((s, r) => s + r.amount, 0) +
          lists.benefitsList.reduce((s, r) => s + r.amount, 0)) *
          12
      ),
    },
    applicability,
    esiAutoEnabled,
    esiCutoff,
    minimumCtc: structureMin,
    hasNegativeBalance,
    balanceAmount,
    complianceError,
    canSave: !complianceError,
    ptState: config.ptState,
    effectiveMonth: effectiveKey.slice(0, 7),
  };
}
